/**
 * useClientPortalBillingSummary
 *
 * Fetches all billing-relevant FHIR resources for the effective portal patient
 * and normalizes them into a single ClientPortalBillingSummary.
 *
 * Resilience strategy:
 *   - Uses Promise.allSettled so one failing resource type does not abort the page.
 *   - Falls back to Encounter service history when Invoice/ChargeItem are missing.
 *   - Avoids duplicate service lines: ChargeItem takes precedence over Encounter.
 *   - dataAvailability flags communicate to the UI what data source was used.
 *   - If every billing search fails, surface an error instead of an empty summary.
 */
import { useMedplum } from '@medplum/react-hooks';
import type { MedplumClient } from '@medplum/core';
import type {
  Account,
  Appointment,
  ChargeItem,
  Coverage,
  DocumentReference,
  Encounter,
  CoverageEligibilityResponse,
  EpisodeOfCare,
  ExplanationOfBenefit,
  Invoice,
  Organization,
} from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';

import { displayLabelForEpisodeType } from '../../../lib/fhirValueSets/sessionAndEpisodeTypes';
import { listPaymentMethods } from '../../../lib/stripeBillingService';
import { useEffectivePortalPatientId } from '../../../hooks/usePortalSubjectPatientId';
import type {
  ClientPortalBillingSummary,
  ClientPortalBillingDocument,
  ClientPortalBillingServiceLine,
  ClientPortalInvoiceSummary,
} from '../utils/clientPortalFhirTypes';
import {
  accountToBillingBalance,
  appointmentToServiceLine,
  buildClientPortalPayments,
  chargeItemToServiceLine,
  coverageToCoverageSummary,
  documentReferenceToBillingDocument,
  encounterToFallbackServiceLine,
  invoiceToClientPortalInvoice,
  safeReferenceId,
} from '../utils/portalBillingFhirAdapters';
import { practitionerIdFromEncounter } from '../utils/portalBillingEncounter';
import { loadPractitionerPreferredLabelMap } from '../../../lib/loadPractitionerPreferredLabels';
import { sortInvoices, sortServiceLines, withResolvedServiceLineKinds } from '../utils/portalBillingModels';
import { savedPaymentMethodToClientPortalSummary } from '../utils/portalPaymentMethodAdapters';
import { buildBillingTimeline } from '@/lib/billing/billingTimeline';
import { filterTimelineForClientPortal } from '@/lib/billing/billingTimelineFilter';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseClientPortalBillingSummaryResult {
  summary: ClientPortalBillingSummary | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function settled<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch {
    return null;
  }
}

const SKIPPED_APPOINTMENT_STATUSES = new Set<Appointment['status']>([
  'cancelled',
  'noshow',
  'entered-in-error',
]);

async function hydrateEncountersForChargeItems(
  medplum: MedplumClient,
  chargeItems: ChargeItem[],
  encounterById: Map<string, Encounter>,
): Promise<void> {
  const missingIds = new Set<string>();
  for (const ci of chargeItems) {
    const id = safeReferenceId(ci.context?.reference, 'Encounter');
    if (id && !encounterById.has(id)) {
      missingIds.add(id);
    }
  }
  await Promise.all(
    [...missingIds].map(async (id) => {
      try {
        const enc = await medplum.readResource('Encounter', id);
        if (enc.id) encounterById.set(enc.id, enc);
      } catch {
        // ChargeItem context may reference an inaccessible encounter.
      }
    }),
  );
}

function coveredAppointmentIds(encounters: Encounter[]): Set<string> {
  const ids = new Set<string>();
  for (const enc of encounters) {
    for (const ref of enc.appointment ?? []) {
      const id = safeReferenceId(ref.reference, 'Appointment');
      if (id) ids.add(id);
    }
  }
  return ids;
}

async function hydrateEpisodeOfCareForEncounters(
  medplum: MedplumClient,
  encounters: Iterable<Encounter>,
  eocById: Map<string, EpisodeOfCare>,
): Promise<void> {
  const missingIds = new Set<string>();
  for (const enc of encounters) {
    for (const ref of enc.episodeOfCare ?? []) {
      const id = safeReferenceId(ref.reference, 'EpisodeOfCare');
      if (id && !eocById.has(id)) missingIds.add(id);
    }
  }
  await Promise.all(
    [...missingIds].map(async (id) => {
      try {
        const eoc = await medplum.readResource('EpisodeOfCare', id);
        if (eoc.id) eocById.set(eoc.id, eoc);
      } catch {
        // Encounter may reference an inaccessible episode.
      }
    }),
  );
}

function episodeOfCareLabelFromEncounter(
  enc: Encounter | undefined,
  eocById: Map<string, EpisodeOfCare>,
): string | undefined {
  if (!enc) return undefined;
  for (const ref of enc.episodeOfCare ?? []) {
    const id = safeReferenceId(ref.reference, 'EpisodeOfCare');
    if (id) {
      const eoc = eocById.get(id);
      if (eoc) return displayLabelForEpisodeType(eoc.type?.[0]);
    }
    const display = ref.display?.trim();
    if (display) return display;
  }
  return undefined;
}

function attachEpisodeLabelsToServiceLines(
  lines: ClientPortalBillingServiceLine[],
  encounterById: Map<string, Encounter>,
  eocById: Map<string, EpisodeOfCare>,
): ClientPortalBillingServiceLine[] {
  return lines.map((line) => {
    if (line.episodeOfCareLabel) return line;
    const enc = line.encounterId ? encounterById.get(line.encounterId) : undefined;
    const label = episodeOfCareLabelFromEncounter(enc, eocById);
    return label ? { ...line, episodeOfCareLabel: label } : line;
  });
}

async function fetchPortalPaymentMethods(
  medplum: MedplumClient,
  patientId: string,
): Promise<ClientPortalBillingSummary['paymentMethods']> {
  try {
    const methods = await listPaymentMethods(medplum, patientId);
    return methods.map(savedPaymentMethodToClientPortalSummary);
  } catch {
    return [];
  }
}

export function useClientPortalBillingSummary(): UseClientPortalBillingSummaryResult {
  const medplum = useMedplum();
  const patientId = useEffectivePortalPatientId();

  const [summary, setSummary] = useState<ClientPortalBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!patientId) {
      setSummary(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const patientRef = `Patient/${patientId}`;

        // Invalidate caches so we get fresh data on each visit.
        medplum.invalidateSearches('Account');
        medplum.invalidateSearches('Invoice');
        medplum.invalidateSearches('ChargeItem');
        medplum.invalidateSearches('Coverage');
        medplum.invalidateSearches('CoverageEligibilityResponse');
        medplum.invalidateSearches('Encounter');
        medplum.invalidateSearches('ExplanationOfBenefit');
        medplum.invalidateSearches('DocumentReference');
        medplum.invalidateSearches('Appointment');

        // Fire all queries in parallel; tolerate individual failures.
        const [
          accounts,
          invoices,
          chargeItems,
          coverages,
          eligibilityResponses,
          encounters,
          eobs,
          docRefs,
          appointments,
        ] = await Promise.all([
          settled(medplum.searchResources('Account', { subject: patientRef, _count: '10' })),
          settled(medplum.searchResources('Invoice', { subject: patientRef, _count: '100', _sort: '-date' })),
          settled(medplum.searchResources('ChargeItem', { subject: patientRef, _count: '200', _sort: '-occurrence' })),
          settled(medplum.searchResources('Coverage', { beneficiary: patientRef, _count: '10' })),
          settled(medplum.searchResources('CoverageEligibilityResponse', { patient: patientRef, _count: '50' })),
          settled(medplum.searchResources('Encounter', { subject: patientRef, _count: '200', _sort: '-date' })),
          settled(medplum.searchResources('ExplanationOfBenefit', { patient: patientRef, _count: '50' })),
          settled(medplum.searchResources('DocumentReference', { subject: patientRef, _count: '100', _sort: '-date' })),
          settled(medplum.searchResources('Appointment', {
            patient: patientRef,
            _sort: '-date',
            _count: '200',
          })),
        ]);

        if (
          [
            accounts,
            invoices,
            chargeItems,
            coverages,
            eligibilityResponses,
            encounters,
            eobs,
            docRefs,
            appointments,
          ].every((result) => result === null)
        ) {
          if (!cancelled) {
            setSummary(null);
            setError('Could not load billing');
          }
          return;
        }

        if (cancelled) return;

        // ── Account / balance ──────────────────────────────────────────────
        const hasAccount = (accounts?.length ?? 0) > 0;
        const balanceDueCents = hasAccount
          ? accountToBillingBalance(accounts![0] as Account)
          : 0;

        // ── Coverage ──────────────────────────────────────────────────────
        const activeCoverages = ((coverages as Coverage[] | null) ?? [])
          .filter((c) => c.status === 'active')
          .sort((a, b) => {
            if (a.order == null && b.order == null) return 0;
            if (a.order == null) return 1;
            if (b.order == null) return -1;
            return a.order - b.order;
          });
        const coverageFetchSucceeded = coverages !== null;
        const hasCoverage = activeCoverages.length > 0;
        const cerList = (eligibilityResponses as CoverageEligibilityResponse[] | null) ?? [];

        const payerOrgIds = new Set<string>();
        for (const cov of activeCoverages) {
          const orgId = safeReferenceId(cov.payor?.[0]?.reference, 'Organization');
          if (orgId) payerOrgIds.add(orgId);
        }
        const payerOrgsById = new Map<string, Organization>();
        await Promise.all(
          [...payerOrgIds].map(async (orgId) => {
            try {
              const org = await medplum.readResource('Organization', orgId);
              if (org.id) payerOrgsById.set(org.id, org);
            } catch {
              // Payer org may be inaccessible even when Coverage is readable.
            }
          }),
        );

        const coverageSummaries = activeCoverages.map((c) => {
          const payerOrgId = safeReferenceId(c.payor?.[0]?.reference, 'Organization');
          return coverageToCoverageSummary(
            c,
            payerOrgId ? payerOrgsById.get(payerOrgId) : undefined,
            cerList,
          );
        });

        // ── Invoices ──────────────────────────────────────────────────────
        const hasInvoices = (invoices?.length ?? 0) > 0;
        const allInvoices: ClientPortalInvoiceSummary[] = (invoices as Invoice[] | null)?.map(
          invoiceToClientPortalInvoice,
        ) ?? [];
        const sorted = sortInvoices(allInvoices);

        const OPEN_STATUSES = new Set(['due', 'overdue', 'partially-paid', 'processing', 'insurance-pending']);
        const openInvoices = sorted.filter((inv) => OPEN_STATUSES.has(inv.status));
        const invoiceHistory = sorted.filter((inv) => !OPEN_STATUSES.has(inv.status));

        const nextDueDate = openInvoices.find((inv) => inv.dueDate)?.dueDate;

        // ── Service lines ─────────────────────────────────────────────────
        const hasChargeItems = (chargeItems?.length ?? 0) > 0;
        const rawEncounters = (encounters as Encounter[] | null) ?? [];

        const rawAppointments = (appointments as Appointment[] | null) ?? [];

        // Resolve practitioner display names for all service-line sources.
        const needIds = new Set<string>();
        for (const enc of rawEncounters) {
          const id = practitionerIdFromEncounter(enc);
          if (id) needIds.add(id);
        }
        for (const ci of (chargeItems as ChargeItem[] | null) ?? []) {
          const id = safeReferenceId(ci.performer?.[0]?.actor?.reference, 'Practitioner');
          if (id) needIds.add(id);
        }
        for (const apt of rawAppointments) {
          const ref = apt.participant?.find((p) =>
            p.actor?.reference?.startsWith('Practitioner/'),
          )?.actor?.reference;
          const id = safeReferenceId(ref, 'Practitioner');
          if (id) needIds.add(id);
        }
        const practitionerMap = await loadPractitionerPreferredLabelMap(medplum, needIds);

        let serviceLines: ClientPortalBillingServiceLine[] = [];
        let usedEncounterFallback = false;

        const encounterById = new Map(
          rawEncounters.flatMap((enc) => (enc.id ? [[enc.id, enc] as const] : [])),
        );

        if (hasChargeItems) {
          await hydrateEncountersForChargeItems(
            medplum,
            chargeItems as ChargeItem[],
            encounterById,
          );

          // Use ChargeItems as the primary billing source.
          const chargeItemLines = (chargeItems as ChargeItem[]).map((ci) => {
            const encId = safeReferenceId(ci.context?.reference, 'Encounter');
            const relatedEncounter = encId ? encounterById.get(encId) : undefined;
            return chargeItemToServiceLine(ci, relatedEncounter, undefined, practitionerMap);
          });

          // Any encounter NOT covered by a ChargeItem is an assessment or
          // non-billed visit (e.g. QuestionnaireResponse-backed encounter).
          // Always include these so assessments appear in the Services tab.
          const billedEncounterIds = new Set(
            chargeItemLines.map((l) => l.encounterId).filter(Boolean),
          );
          const unbilledLines = rawEncounters
            .filter((enc) => enc.id && !billedEncounterIds.has(enc.id))
            .map((enc) => encounterToFallbackServiceLine(enc, practitionerMap));

          serviceLines = [...chargeItemLines, ...unbilledLines];
          if (unbilledLines.length > 0) {
            usedEncounterFallback = true;
          }
        } else {
          // No ChargeItems — fall back entirely to Encounter history (includes assessments).
          if (rawEncounters.length > 0) {
            usedEncounterFallback = true;
            serviceLines = rawEncounters.map((enc) =>
              encounterToFallbackServiceLine(enc, practitionerMap),
            );
          }
        }

        const billedAppointmentIds = coveredAppointmentIds(rawEncounters);
        const appointmentLines = rawAppointments
          .filter((apt) => apt.id && !billedAppointmentIds.has(apt.id))
          .filter((apt) => !apt.status || !SKIPPED_APPOINTMENT_STATUSES.has(apt.status))
          .map((apt) => appointmentToServiceLine(apt, practitionerMap));

        if (appointmentLines.length > 0) {
          usedEncounterFallback = true;
          serviceLines = [...serviceLines, ...appointmentLines];
        }

        const eocById = new Map<string, EpisodeOfCare>();
        await hydrateEpisodeOfCareForEncounters(medplum, encounterById.values(), eocById);
        serviceLines = attachEpisodeLabelsToServiceLines(serviceLines, encounterById, eocById);
        serviceLines = withResolvedServiceLineKinds(sortServiceLines(serviceLines));

        const allInvoiceSummaries = [...openInvoices, ...invoiceHistory];
        const recentPayments = buildClientPortalPayments({
          chargeItems: (chargeItems as ChargeItem[]) ?? [],
          serviceLines,
          invoices: allInvoiceSummaries,
        });

        const billingTimeline = filterTimelineForClientPortal(
          buildBillingTimeline({
            chargeItems: (chargeItems as ChargeItem[]) ?? [],
            encounters: rawEncounters,
            invoices: (invoices as Invoice[]) ?? [],
            encounterById,
          }),
        );

        // ── Documents ─────────────────────────────────────────────────────
        const billingKinds = new Set<ClientPortalBillingDocument['kind']>([
          'statement', 'superbill', 'eob', 'gfe', 'receipt', 'invoice',
        ]);
        const rawDocRefs = (docRefs as DocumentReference[] | null) ?? [];
        const billingDocs: ClientPortalBillingDocument[] = rawDocRefs
          .map(documentReferenceToBillingDocument)
          .filter((d) => billingKinds.has(d.kind));

        const hasDocuments = billingDocs.length > 0;
        const statements = billingDocs.filter((d) => d.kind === 'statement');
        const superbills = billingDocs.filter((d) => d.kind === 'superbill');
        const eobDocs = billingDocs.filter((d) => d.kind === 'eob');
        const gfes = billingDocs.filter((d) => d.kind === 'gfe');

        // ── Insurance pending from EOBs ────────────────────────────────────
        let insurancePendingCents: number | undefined;
        const rawEobs = (eobs as ExplanationOfBenefit[] | null) ?? [];
        if (rawEobs.length > 0) {
          const pendingEob = rawEobs.find((e) => e.outcome === 'queued');
          if (pendingEob) {
            // Rough heuristic: sum EOB total for queued claims.
            insurancePendingCents = Math.round(
              (pendingEob.total?.reduce((s, t) => s + (t.amount?.value ?? 0), 0) ?? 0) * 100,
            );
          }
        }

        // ── Payment methods (Stripe) ───────────────────────────────────────
        const paymentMethods = patientId
          ? await fetchPortalPaymentMethods(medplum, patientId)
          : [];

        // ── Assemble summary ──────────────────────────────────────────────
        const result: ClientPortalBillingSummary = {
          patientId,
          balanceDueCents,
          insurancePendingCents,
          nextDueDate,
          openInvoices,
          invoiceHistory,
          serviceLines,
          recentPayments,
          insurancePayments: [],
          documents: billingDocs,
          statements,
          superbills,
          eobs: eobDocs,
          gfes,
          billingTimeline,
          coverageSummaries,
          paymentMethods,
          dataAvailability: {
            hasAccount,
            hasInvoices,
            hasChargeItems,
            hasCoverage,
            coverageFetchSucceeded,
            hasDocuments,
            usedEncounterFallback,
          },
        };

        if (!cancelled) {
          setSummary(result);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[useClientPortalBillingSummary] Failed to build billing summary:', err);
          setSummary(null);
          setError(err instanceof Error ? err.message : 'Could not load billing');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, tick]);

  return { summary, loading, error, refresh };
}
